import React from 'react';
import { Box, BoxProps, ButtonBase } from '@mui/material';
import Txt from '../design-system/Txt';

/**
 * The instrument panel (redesign, 2026-11-03): the four parts every screen is
 * built from, so the Dashboard, Food Log, Analysis, Supplements, Labs and
 * Exercise read as one app.
 *
 *   1. Readout      mono label · big number with a soft glow · change · one sentence
 *   2. SegmentBar   40 segments for a day's fuel, 10 for any score or goal
 *   3. TrendTile    name · average · change · line          (Analysis)
 *   4. Colour means one thing (INK below)
 *
 * Text uses Txt, not Typography: global.css resizes Typography on phones.
 */

export const INK = {
  ground: '#0a0e14',
  card: 'var(--bg-card)',
  cyan: '#00E0FF', // FuelTron, protein, anything you can tap
  green: '#00E676', // on track, calories left, in range
  amber: '#FFD740', // carbs, and worth a look
  purple: '#CE93D8', // fat, supplements and labs
  red: '#FF1744', // only over a limit, or an error
  text: '#FFFFFF',
  soft: 'rgba(255,255,255,0.78)',
  muted: 'rgba(255,255,255,0.6)',
  faint: 'rgba(255,255,255,0.08)',
} as const;

export const MONO = "'JetBrains Mono', monospace";

/** Score tone: green at 80+, amber below. Never red for a score. */
export const scoreTone = (score: number) => (score >= 80 ? INK.green : INK.amber);

// ── Card ────────────────────────────────────────────────────────────────────

interface InstrumentCardProps extends Omit<BoxProps, 'onClick'> {
  /** Border tint; defaults to the cyan family. */
  accent?: string;
  /** The glowing 2px line along the top: the page's lead card only. */
  lead?: boolean;
  /** Dashed border, for a place that fills in later. */
  dashed?: boolean;
  onClick?: () => void;
}

/** A Box acting as a card (so global.css's Paper rules don't touch it). 12px radius like every card. */
export const InstrumentCard: React.FC<InstrumentCardProps> = ({ accent = INK.cyan, lead, dashed, onClick, sx, children, ...rest }) => {
  const body = (
    <Box
      {...rest}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 1.5,
        background: dashed ? `${accent}08` : `linear-gradient(180deg, ${accent}0A 0%, transparent 56px), ${INK.card}`,
        border: `1px ${dashed ? 'dashed' : 'solid'} ${accent}${dashed ? '55' : lead ? '59' : '40'}`,
        boxShadow: dashed ? 'none' : `0 0 18px ${accent}0F`,
        p: { xs: 1.5, sm: 2 },
        textAlign: 'left',
        width: '100%',
        boxSizing: 'border-box',
        color: 'inherit',
        ...(lead ? {
          '&::before': {
            content: '""', position: 'absolute', top: 0, left: 16, right: 16, height: 2,
            background: accent, boxShadow: `0 0 12px ${accent}, 0 0 4px ${accent}`,
          },
        } : {}),
        ...(sx as object),
      }}
    >
      {children}
    </Box>
  );
  if (!onClick) return body;
  return (
    <ButtonBase onClick={onClick} sx={{ display: 'block', width: '100%', borderRadius: 1.5, textAlign: 'left' }}>
      {body}
    </ButtonBase>
  );
};

// ── Labels ──────────────────────────────────────────────────────────────────

export const MonoLabel: React.FC<{ color?: string; children: React.ReactNode; sx?: object }> = ({ color = INK.cyan, children, sx }) => (
  <Txt component="span" sx={{ fontFamily: MONO, fontSize: '0.7rem', fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color, ...sx }}>
    {children}
  </Txt>
);

/** Label on the left, a small mono note on the right. */
export const CardHead: React.FC<{ label: React.ReactNode; color?: string; meta?: React.ReactNode }> = ({ label, color, meta }) => (
  <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
    <MonoLabel color={color}>{label}</MonoLabel>
    {meta != null && <Txt component="span" sx={{ fontFamily: MONO, fontSize: '0.7rem', color: INK.muted, whiteSpace: 'nowrap' }}>{meta}</Txt>}
  </Box>
);

// ── Segments ────────────────────────────────────────────────────────────────

interface SegmentBarProps {
  /** One entry per segment: a colour lights it, null leaves it dark. */
  segments: Array<string | null>;
  height?: number;
  gap?: number;
  /** Outline-only segments: a place that fills in later (day one). */
  ghost?: boolean;
  label?: string;
}

export const SegmentBar: React.FC<SegmentBarProps> = ({ segments, height = 8, gap = 3, ghost, label }) => (
  <Box
    role={label ? 'img' : undefined}
    aria-label={label}
    aria-hidden={label ? undefined : true}
    sx={{ display: 'grid', gridTemplateColumns: `repeat(${segments.length}, minmax(0, 1fr))`, gap: `${gap}px` }}
  >
    {segments.map((c, i) => (
      <Box
        key={i}
        sx={ghost
          ? { height, borderRadius: '2px', border: '1px solid rgba(255,255,255,0.14)', boxSizing: 'border-box' }
          : { height, borderRadius: '2px', background: c || INK.faint, boxShadow: c ? `0 0 6px ${c}66` : 'none', transition: 'background 0.3s' }}
      />
    ))}
  </Box>
);

/** `lit` of `count` segments in one colour. */
export const fill = (count: number, lit: number, color: string): Array<string | null> =>
  Array.from({ length: count }, (_, i) => (i < Math.max(0, Math.min(count, Math.round(lit))) ? color : null));

/**
 * The 40-segment fuel gauge: each segment is 1/40 of the calorie goal, lit in
 * the order protein, carbs, fat by where the calories came from. Past the goal
 * every segment is lit and the overflow share turns red.
 */
export function fuelSegments(calories: number, goal: number, protein: number, carbs: number, fat: number, count = 40): Array<string | null> {
  if (goal <= 0) return Array(count).fill(null);
  const lit = Math.min(count, Math.round((calories / goal) * count));
  const pk = protein * 4, ck = carbs * 4, fk = fat * 9;
  const macroTotal = pk + ck + fk;
  const over = calories > goal ? Math.min(count, Math.round(((calories - goal) / calories) * count)) : 0;
  const out: Array<string | null> = [];
  const pN = macroTotal > 0 ? Math.round((pk / macroTotal) * lit) : 0;
  const cN = macroTotal > 0 ? Math.round((ck / macroTotal) * lit) : 0;
  for (let i = 0; i < count; i++) {
    if (i >= lit) out.push(null);
    else if (i >= count - over) out.push(INK.red);
    else if (macroTotal <= 0) out.push(INK.cyan);
    else if (i < pN) out.push(INK.cyan);
    else if (i < pN + cN) out.push(INK.amber);
    else out.push(INK.purple);
  }
  return out;
}

// ── Readout ─────────────────────────────────────────────────────────────────

interface ReadoutProps {
  label: React.ReactNode;
  labelColor?: string;
  meta?: React.ReactNode;
  /** The big number, already formatted. null shows "--" dimmed. */
  value: React.ReactNode | null;
  unit?: React.ReactNode;
  valueColor?: string;
  glow?: string;
  size?: 'hero' | 'large' | 'medium';
  delta?: { text: string; tone?: 'good' | 'watch' | 'flat' };
  deltaNote?: string;
  sentence?: React.ReactNode;
}

const SIZES = { hero: '3rem', large: '2.5rem', medium: '1.9rem' };

export const Readout: React.FC<ReadoutProps> = ({ label, labelColor, meta, value, unit, valueColor = INK.text, glow, size = 'large', delta, deltaNote, sentence }) => {
  const toneColor = delta?.tone === 'good' ? INK.green : delta?.tone === 'watch' ? INK.amber : INK.muted;
  return (
    <Box>
      <CardHead label={label} color={labelColor} meta={meta} />
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
        <Txt component="span" sx={{ fontFamily: MONO, fontSize: SIZES[size], fontWeight: 800, lineHeight: 1, color: value == null ? 'rgba(255,255,255,0.2)' : valueColor, textShadow: value != null && glow ? `0 0 18px ${glow}8C, 0 0 2px ${glow}` : 'none' }}>
          {value == null ? '--' : value}
        </Txt>
        {unit != null && <Txt component="span" sx={{ fontFamily: MONO, fontSize: '0.9rem', color: 'rgba(255,255,255,0.5)' }}>{unit}</Txt>}
        {delta && (
          <Txt component="span" sx={{ ml: 'auto', textAlign: 'right' }}>
            <Txt component="span" sx={{ display: 'block', fontFamily: MONO, fontSize: '0.9rem', fontWeight: 700, color: toneColor }}>{delta.text}</Txt>
            {deltaNote && <Txt component="span" sx={{ display: 'block', fontSize: '0.7rem', color: INK.muted }}>{deltaNote}</Txt>}
          </Txt>
        )}
      </Box>
      {sentence && <Txt sx={{ mt: 0.75, fontSize: '0.8rem', lineHeight: 1.4, color: INK.soft }}>{sentence}</Txt>}
    </Box>
  );
};

// ── Rows ────────────────────────────────────────────────────────────────────

/** Label · 10-segment bar · value. With no value, `action` takes the value's place (day one). */
export const PillarRow: React.FC<{ label: string; score: number | null; display?: string; color?: string; labelWidth?: number; action?: { label: string; onClick: () => void } }> = ({ label, score, display, color, labelWidth = 84, action }) => {
  const has = score != null;
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: `${labelWidth}px minmax(0, 1fr) auto`, alignItems: 'center', gap: 1, minHeight: 22 }}>
      <Txt component="span" sx={{ fontFamily: MONO, fontSize: '0.7rem', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: 'rgba(255,255,255,0.72)', whiteSpace: 'nowrap' }}>{label}</Txt>
      <SegmentBar segments={has ? fill(10, (score as number) / 10, color || scoreTone(score as number)) : Array(10).fill(null)} height={7} ghost={!has} label={has ? `${label} ${display ?? score}` : undefined} />
      {has ? (
        <Txt component="span" sx={{ fontFamily: MONO, fontSize: '0.8rem', fontWeight: 700, color: INK.text, textAlign: 'right', minWidth: 26 }}>{display ?? score}</Txt>
      ) : action ? (
        <ButtonBase onClick={action.onClick} sx={{ minHeight: 30, px: 0.5, my: -0.5, fontFamily: MONO, fontSize: '0.7rem', fontWeight: 700, color: INK.cyan, whiteSpace: 'nowrap' }}>{action.label}</ButtonBase>
      ) : (
        <Txt component="span" sx={{ fontFamily: MONO, fontSize: '0.8rem', color: 'rgba(255,255,255,0.3)', textAlign: 'right', minWidth: 26 }}>--</Txt>
      )}
    </Box>
  );
};

/** Macro panel: label · value / goal · 10 segments. */
export const MacroPanel: React.FC<{ label: string; color: string; value: number; goal: number; unit?: string; showValue?: boolean }> = ({ label, color, value, goal, unit = 'g', showValue = true }) => (
  <Box sx={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '8px', px: 0.75, py: 0.75, textAlign: 'center', minWidth: 0 }}>
    <Txt sx={{ fontFamily: MONO, fontSize: '0.6rem', fontWeight: 700, letterSpacing: 1.5, color }}>{label}</Txt>
    <Txt sx={{ fontFamily: MONO, fontSize: '0.9rem', fontWeight: 700, color: INK.text, my: 0.25, whiteSpace: 'nowrap' }}>
      {showValue ? Math.round(value) : Math.round(goal)}
      <Txt component="span" sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)' }}>{showValue ? ` /${Math.round(goal)}${unit}` : ` ${unit}`}</Txt>
    </Txt>
    <SegmentBar segments={fill(10, goal > 0 ? (value / goal) * 10 : 0, color)} height={6} gap={2} ghost={!showValue} />
  </Box>
);

/** Buttons in the panel style: one primary (filled cyan) per card, the rest outlined. */
export const PanelButton: React.FC<{ variant?: 'primary' | 'outlined' | 'quiet'; onClick?: () => void; children: React.ReactNode; fullWidth?: boolean; icon?: React.ReactNode; sx?: object; ariaLabel?: string; disabled?: boolean }> = ({ variant = 'outlined', onClick, children, fullWidth, icon, sx, ariaLabel, disabled }) => (
  <ButtonBase
    onClick={onClick}
    disabled={disabled}
    aria-label={ariaLabel}
    sx={{
      minHeight: 36, px: 1.75, borderRadius: '8px', gap: 0.75,
      width: fullWidth ? '100%' : 'auto',
      fontFamily: 'Inter, sans-serif', fontSize: '0.8rem', fontWeight: variant === 'primary' ? 700 : 600,
      ...(variant === 'primary'
        ? { background: INK.cyan, color: INK.ground, '&:hover': { background: '#5CEBFF' } }
        : variant === 'outlined'
          ? { border: '1px solid rgba(0,224,255,0.5)', color: INK.cyan, '&:hover': { background: 'rgba(0,224,255,0.08)' } }
          : { color: INK.muted, '&:hover': { color: INK.text } }),
      '&.Mui-disabled': { opacity: 0.45 },
      ...sx,
    }}
  >
    {icon}{children}
  </ButtonBase>
);

// ── Page title ──────────────────────────────────────────────────────────────

/**
 * The page title every screen uses: glowing icon + mono uppercase title in the
 * FuelTron gradient, with the page's actions on the right. PageHeader renders
 * this too, so pages not yet rebuilt carry the same top.
 */
/** Glowing icon + gradient mono title (the h1). Used by PageTitle and by headers that lay out their own row. */
export const TitleText: React.FC<{ title: string; icon?: React.ReactNode; gradient?: string; sx?: object }> = ({ title, icon, gradient = `${INK.cyan}, ${INK.green}`, sx }) => {
  const first = gradient.split(',')[0].trim();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0, ...sx }}>
      {icon && (
        <Box aria-hidden sx={{ display: 'flex', flexShrink: 0, color: first, filter: `drop-shadow(0 0 6px ${first}99)`, '& svg': { width: 22, height: 22, fontSize: 22, color: first } }}>{icon}</Box>
      )}
      <Txt component="h1" sx={{
        m: 0, fontFamily: MONO, fontWeight: 800, letterSpacing: 2.5, textTransform: 'uppercase', whiteSpace: 'nowrap',
        fontSize: { xs: '1rem', sm: '1.25rem' }, lineHeight: 1.2,
        background: `linear-gradient(135deg, ${gradient})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
        filter: `drop-shadow(0 0 10px ${first}55)`,
      }}>{title}</Txt>
    </Box>
  );
};

export const PageTitle: React.FC<{ title: string; icon?: React.ReactNode; gradient?: string; right?: React.ReactNode; mb?: number }> = ({ title, icon, gradient, right, mb = 1.5 }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb }}>
    <TitleText title={title} icon={icon} gradient={gradient} />
    {right && <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>{right}</Box>}
  </Box>
);
